import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "nc_category_images_cache_v1";

// Cache shape: { slug: [url1, url2, url3, url4] }
let _cache = null;
let _loaded = false; // true once at least one fetch (or cache hydration) completed
let _inflight = null;
const _listeners = new Set();

// Hydrate from localStorage synchronously on first import — instant paint on repeat visits.
try {
  if (typeof window !== "undefined" && window.localStorage) {
    const raw = window.localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        _cache = parsed;
        _loaded = true; // we have *something* to show immediately
      }
    }
  }
} catch (_) { /* ignore quota / parse errors */ }

function persist(data) {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LS_KEY, JSON.stringify(data));
    }
  } catch (_) { /* quota exceeded — ignore */ }
}

async function load(force = false) {
  if (_cache && !force && _loaded) {
    // Return cached but always trigger a silent background refresh to catch admin updates.
    if (!_inflight) {
      _inflight = _fetch();
    }
    return _cache;
  }
  if (_inflight) return _inflight;
  _inflight = _fetch();
  return _inflight;
}

function _fetch() {
  return axios.get(`${API}/category-images`)
    .then((r) => {
      const data = r.data || {};
      const norm = {};
      Object.keys(data).forEach((k) => {
        const v = data[k];
        if (Array.isArray(v)) norm[k] = v.filter(Boolean);
        else if (typeof v === "string" && v) norm[k] = [v];
        else norm[k] = [];
      });
      _cache = norm;
      _loaded = true;
      _inflight = null;
      persist(_cache);
      _listeners.forEach((fn) => fn(_cache));
      return _cache;
    })
    .catch(() => {
      _inflight = null;
      if (!_cache) _cache = {};
      _loaded = true;
      return _cache;
    });
}

export function getCategoryImages(slug) {
  if (_cache && Array.isArray(_cache[slug])) return _cache[slug];
  return [];
}

export function getCategoryImage(slug, fallback) {
  const arr = getCategoryImages(slug);
  return (arr && arr[0]) || fallback;
}

export function refreshCategoryImages() {
  return load(true);
}

// Hook returns a normalized object {slug: [urls]} AND keeps backward-compat
// by exposing the first image directly when accessed via the proxy.
// `overrides.loaded` returns whether at least one fetch (or cache hydration) has completed.
export default function useCategoryImages() {
  const [images, setImages] = useState(_cache || {});
  const [loaded, setLoaded] = useState(_loaded);
  useEffect(() => {
    let mounted = true;
    load().then((data) => {
      if (mounted) { setImages({ ...data }); setLoaded(_loaded); }
    });
    const fn = (data) => { if (mounted) { setImages({ ...data }); setLoaded(true); } };
    _listeners.add(fn);
    return () => { mounted = false; _listeners.delete(fn); };
  }, []);
  // Proxy: overrides[slug] → first image (string), overrides.all → full arrays, overrides.loaded → bool
  return new Proxy(images, {
    get(target, prop) {
      if (prop === "all") return target;
      if (prop === "loaded") return loaded;
      const arr = target[prop];
      if (Array.isArray(arr)) return arr[0];
      return arr;
    },
    has(target, prop) { return prop in target || prop === "all" || prop === "loaded"; },
    ownKeys(target) { return Reflect.ownKeys(target); },
    getOwnPropertyDescriptor(target, prop) { return Object.getOwnPropertyDescriptor(target, prop); },
  });
}
